import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const body = new URLSearchParams();
      body.append('email', email.trim());
      const res = await fetch('https://readdy.ai/api/form/d8ldmhtk31rj0i4c6qc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection.');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-start gap-3 py-5 px-5 bg-white/10 rounded-2xl border border-white/20">
        <div className="w-10 h-10 rounded-full bg-green-400/20 flex items-center justify-center">
          <i className="ri-check-line text-green-400 text-lg"></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-white mb-1">You&apos;re subscribed!</p>
          <p className="text-xs text-cream-300 leading-relaxed">
            Thanks for subscribing! Your complimentary publishing guide is on its way to your inbox.
          </p>
        </div>
        <button
          onClick={() => setStatus('idle')}
          className="text-xs text-cream-300 hover:text-white underline underline-offset-2 transition-colors duration-200"
        >
          Subscribe another email
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-3"
    >
      <div className="flex items-center bg-white/10 rounded-md overflow-hidden group transition-all duration-300 focus-within:bg-white/20 focus-within:ring-1 focus-within:ring-white/30">
        <span className="w-10 h-10 flex items-center justify-center shrink-0">
          <i className="ri-mail-line text-white/70 text-sm"></i>
        </span>
        <input
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter Your Email Address"
          className="flex-1 bg-transparent px-3 py-3 text-sm text-white placeholder-white/50 outline-none"
          disabled={status === 'loading'}
        />
      </div>

      {status === 'error' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/20 border border-red-400/30 rounded-lg">
          <i className="ri-error-warning-line text-red-400 text-sm shrink-0"></i>
          <p className="text-xs text-red-300">{errorMsg}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading' || !email.trim()}
        className="w-full py-3 text-sm font-medium text-navy-900 bg-white rounded-full hover:bg-cream-100 transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
      >
        {status === 'loading' ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base"></i>
            Subscribing...
          </>
        ) : (
          'Subscribe to Newsletter'
        )}
      </button>
    </form>
  );
}

export default function Footer() {
  const footerAnim = useScrollAnimation({ threshold: 0.2, rootMargin: '0px 0px -20px 0px' });

  return (
    <footer className="bg-navy-900 text-white">
      {/* Main Footer */}
      <div className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div ref={footerAnim.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12 animate-on-scroll ${footerAnim.isVisible ? 'animate-visible' : ''}`}>
          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Info</h4>
            <div className="space-y-4">
              <a
                href="tel:1-888-549-0988"
                className="flex items-start gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group"
              >
                <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110">
                  <img
                    src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Phone.svg"
                    alt="Phone"
                    className="w-4 h-4"
                  />
                </span>
                <span>1-888-549-0988</span>
              </a>
              <a
                href="mailto:info@leavittpeakpress.com"
                className="flex items-start gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group"
              >
                <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110">
                  <img
                    src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Mail.svg"
                    alt="Email"
                    className="w-4 h-4"
                  />
                </span>
                <span>info@leavittpeakpress.com</span>
              </a>
              <div className="flex items-start gap-3 text-sm text-cream-300 group">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110">
                  <img
                    src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/building.svg"
                    alt="Address"
                    className="w-4 h-4"
                  />
                </span>
                <span>17777 Center Court Drive, Ste 600, Cerritos, CA 90703</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-cream-300 group">
                <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110">
                  <img
                    src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/mailbox.svg"
                    alt="Address"
                    className="w-4 h-4"
                  />
                </span>
                <span>17901 Pioneer Blvd Ste L. #298, Artesia, CA, 90701</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/" className="flex items-center gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <img src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/http://leavittpeakpress.com/wp-content/uploads/2025/02/home.svg" alt="Home" className="w-4 h-4" />
                </span>
                Home
              </Link>
              <Link to="/about-us" className="flex items-center gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <img src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/http://leavittpeakpress.com/wp-content/uploads/2025/02/info.svg" alt="About" className="w-4 h-4" />
                </span>
                About Us
              </Link>
              <Link to="/events/verified-participation" className="flex items-center gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-gold-400/15 text-gold-400 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-shield-check-line text-xs"></i>
                </span>
                Verified Participation
              </Link>
              <Link to="/about-us#our-honest-position" className="flex items-center gap-3 text-sm text-gold-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-gold-400/15 text-gold-400 transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-chat-quote-line text-xs"></i>
                </span>
                Read our honest position
              </Link>
              <Link to="/services" className="flex items-center gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <img src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/http://leavittpeakpress.com/wp-content/uploads/2025/02/box.svg" alt="Services" className="w-4 h-4" />
                </span>
                Services
              </Link>
              <Link to="/events" className="flex items-center gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <img src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/http://leavittpeakpress.com/wp-content/uploads/2025/02/box.svg" alt="Events" className="w-4 h-4" />
                </span>
                Events
              </Link>
              <Link to="/ratings" className="flex items-center gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <img src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/http://leavittpeakpress.com/wp-content/uploads/2025/02/info.svg" alt="Ratings" className="w-4 h-4" />
                </span>
                Ratings
              </Link>
              <Link to="/contact-us" className="flex items-center gap-3 text-sm text-cream-300 hover:text-white transition-all duration-300 group">
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <img src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/http://leavittpeakpress.com/wp-content/uploads/2025/02/Phone.svg" alt="Contact" className="w-4 h-4" />
                </span>
                Contact Us
              </Link>
            </nav>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Subscribe to Newsletter</h4>
            <p className="text-sm text-cream-300 mb-4">
              Kindly share your email address with us so that we can send you a complimentary publishing guide.
            </p>
            <NewsletterForm />

            {/* Follow Us */}
            <div className="mt-8">
              <h4 className="text-lg font-semibold mb-4">Follow Us</h4>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/LeavittPeakPress"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Leavitt Peak Press on Facebook"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#1877F2] flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                >
                  <i className="ri-facebook-fill text-white text-lg group-hover:text-white transition-colors duration-300"></i>
                </a>
                <a
                  href="https://www.instagram.com/leavittpeakpress"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Leavitt Peak Press on Instagram"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                >
                  <i className="ri-instagram-line text-white text-lg group-hover:text-white transition-colors duration-300"></i>
                </a>
                <a
                  href="https://x.com/LeavittPeakPr"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Leavitt Peak Press on X"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-black flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                >
                  <i className="ri-twitter-x-fill text-white text-lg group-hover:text-white transition-colors duration-300"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Accreditation Logos */}
        <div className="flex items-center gap-6 mt-12 pt-8 border-t border-white/10">
          <Link to="/ratings" className="transition-transform duration-500 hover:scale-110" aria-label="View our BBB A+ Rating">
            <img
              src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/BBB-A-Logo.png"
              alt="BBB A+ Rating"
              className="h-12 w-auto"
            />
          </Link>
          <Link to="/ratings" className="transition-transform duration-500 hover:scale-110" aria-label="View our IPG Membership details">
            <img
              src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/1fcbdb1f-0ca9-4617-be32-aa6039855d10.jpg"
              alt="IPG"
              className="h-12 w-auto"
            />
          </Link>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="w-full px-4 md:px-6 lg:px-8 py-6 border-t border-white/10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-cream-300">Copyright &copy; 2025. All Rights Reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/terms-and-conditions" className="text-sm text-cream-300 hover:text-white transition-colors duration-300">
              Terms and Conditions
            </Link>
            <Link to="/privacy-policy" className="text-sm text-cream-300 hover:text-white transition-colors duration-300">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}